import React, { FC } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MuiButton from "@mui/material/Button";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { CategoryFormData } from "./validation.schema";

type Props = {
  itemIndex: number;
  onRemoveItem: () => void;
  canRemoveItem: boolean;
  languageOptions: { key: string; value: string }[];
};

const EMPTY_LANGUAGE = { language: "", name: "", description: "" };

const CategoryItemFields: FC<Props> = ({
  itemIndex,
  onRemoveItem,
  canRemoveItem,
  languageOptions,
}) => {
  const theme = useAppTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext<CategoryFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `categories.${itemIndex}.languages` as const,
  });

  const itemErrors = errors.categories?.[itemIndex];
  const languagesError = itemErrors?.languages;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="bodyMedium" sx={{ fontWeight: 600 }}>
          Item #{itemIndex + 1}
        </Typography>
        <IconButton
          size="small"
          disabled={!canRemoveItem}
          onClick={onRemoveItem}
          aria-label="remove item"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={"16px"}>
        <Grid item xs={12} sm={3}>
          <Controller
            name={`categories.${itemIndex}.status` as const}
            control={control}
            render={({ field }) => (
              <Select
                handleChange={(value) => field.onChange(value)}
                data={[
                  { key: "ACTIVE", value: "Active" },
                  { key: "INACTIVE", value: "Inactive" },
                ]}
                value={field.value as string}
                label="Item status"
                id={`category-item-status-${itemIndex}`}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name={`categories.${itemIndex}.icon` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`category-item-icon-${itemIndex}`}
                label="Icon"
                variant="outlined"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name={`categories.${itemIndex}.hexaColor` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`category-item-hexacolor-${itemIndex}`}
                label="Hexa color"
                variant="outlined"
                placeholder="#FFFFFF"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name={`categories.${itemIndex}.textColor` as const}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={`category-item-textcolor-${itemIndex}`}
                label="Text color"
                variant="outlined"
                placeholder="#000000"
                value={field.value}
                onChange={(value) => field.onChange(value)}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="bodyMedium" sx={{ fontWeight: 500 }}>
              Languages *
            </Typography>
            <MuiButton
              size="small"
              variant="text"
              startIcon={<AddIcon />}
              onClick={() => append(EMPTY_LANGUAGE)}
            >
              Add language
            </MuiButton>
          </Box>
          {typeof languagesError?.message === "string" && (
            <Typography
              variant="bodySmall"
              sx={{ color: theme.palette.error.main, mb: 1 }}
            >
              {languagesError.message}
            </Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {fields.map((langField, langIndex) => {
              const langErrors = itemErrors?.languages?.[langIndex];
              return (
                <Grid container spacing={"12px"} key={langField.id}>
                  <Grid item xs={12} sm={3}>
                    <Controller
                      name={
                        `categories.${itemIndex}.languages.${langIndex}.language` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <Select
                          handleChange={(value) => field.onChange(value)}
                          data={languageOptions}
                          value={field.value as string}
                          label="Language"
                          id={`category-lang-${itemIndex}-${langIndex}`}
                          required
                          error={!!langErrors?.language}
                          errorMessage={langErrors?.language?.message || ""}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Controller
                      name={
                        `categories.${itemIndex}.languages.${langIndex}.name` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`category-name-${itemIndex}-${langIndex}`}
                          label="Name"
                          variant="outlined"
                          required
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          error={!!langErrors?.name}
                          helperText={langErrors?.name?.message || null}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={11} sm={4}>
                    <Controller
                      name={
                        `categories.${itemIndex}.languages.${langIndex}.description` as const
                      }
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id={`category-desc-${itemIndex}-${langIndex}`}
                          label="Description"
                          variant="outlined"
                          value={field.value}
                          onChange={(value) => field.onChange(value)}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sm={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <IconButton
                      size="small"
                      disabled={fields.length <= 1}
                      onClick={() => remove(langIndex)}
                      aria-label="remove language"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CategoryItemFields;
